'use client';

import { amplitudeSendEvent, getScreenName } from '@musinsa/frontend-utils';
import { LoadingDark, Padding, Typography } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';
import type { LikedProductService } from '@musinsa/services-curator';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { useCuratorBroadcastPublisher } from '../../common';
import { useLikedProductsInfiniteQuery } from '../../common/hooks';
import { curatorQueries } from '../../query-factory';
import { SNOWPLOW_SCHEME } from '../../snowplow';
import { COLUMN_COUNT, chunkArray } from '../utils';
import { buildSelectItemGtmAttr } from '../utils/buildSelectItemGtmAttr';
import { mapProductItemToCurationLikeLog } from '../utils/mapProductItemToCurationLikeLog';
import { EmptyProductList } from './EmptyProductList';
import type { BaseProductImageItem } from './variants/BaseProductImageItem';
import { SearchedProduct } from './variants/SearchedProduct';

interface Props {
  className?: string;
  checkedProducts: BaseProductImageItem[];
  onClickCheckbox: (product: LikedProductService) => void;
  /** 무신사 좋아요/해제 Amplitude용 (mss `curatorMyShop.clickGoodsLike`) */
  curatorId: string;
  onEmptyStateChange?: (isEmpty: boolean) => void;
}

export function SelectableVirtualizedLikedProductList({
  className,
  checkedProducts,
  onClickCheckbox,
  curatorId,
  onEmptyStateChange,
}: Props) {
  const {
    products,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isPending,
    isError,
    removeProductFromCache,
  } = useLikedProductsInfiniteQuery({ size: 20 });

  // 3개씩 그룹화된 행들
  const productRows = useMemo(() => {
    if (!products || products.length === 0) return [];
    return chunkArray(products, COLUMN_COUNT);
  }, [products]);

  const onRangeChanged = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { triggerQuerySync } = useCuratorBroadcastPublisher();
  const { isWebview, service } = useDeviceInfo();

  const handleClickLikeButton = useCallback(
    (product: LikedProductService, listIndex: number, nextIsLiked: boolean) => {
      if (service === SERVICE.MUSINSA && curatorId) {
        const payload = mapProductItemToCurationLikeLog(product);
        if (nextIsLiked) {
          amplitudeSendEvent({
            action_name: 'like_product',
            component_name: 'product',
            event_name: 'like_product_product',
            from: getScreenName(),
            id: curatorId,
            index: listIndex,
            section_name: 'curator_curationfeed',
            type: 'goods',
            ...payload,
          });
        } else {
          amplitudeSendEvent({
            action_name: 'unlike_product',
            component_name: 'product',
            event_name: 'unlike_product_product',
            from: getScreenName(),
            id: curatorId,
            index: listIndex,
            section_name: 'curator_curationfeed',
            type: 'goods',
            ...payload,
          });
        }
      }

      if (service === SERVICE['29CM'] && nextIsLiked) {
        track({
          schema: SNOWPLOW_SCHEME.LIKE_ITEM,
          data: {
            page_name: 'curator_select_product',
            section_name: 'LikedProducts',
            section_title: '좋아요한 상품',
            position: listIndex + 1,
            item_no: Number(product.goodsNo),
            item_name: product.goodsName,
          },
        });
      }

      if (!nextIsLiked) {
        removeProductFromCache(product.goodsNo);
      }
      triggerQuerySync({
        queryKeys: [curatorQueries.curations.myList._def],
      });
    },
    [curatorId, triggerQuerySync, removeProductFromCache, service],
  );

  const renderItemContent = useCallback(
    (index: number) => {
      const row = productRows[index];

      // NOTE: react-virtuoso "Zero-sized element" 경고 방지를 위해 빈 placeholder 반환
      if (!row) return <div style={{ minHeight: 1 }} />;

      return (
        <div className="grid grid-cols-3 bg-neutral-color-white">
          {row.map((product, columnIndex) => {
            const listIndex = index * COLUMN_COUNT + columnIndex;
            const gtmAttr =
              service === SERVICE.MUSINSA ? buildSelectItemGtmAttr(product, listIndex) : undefined;
            return (
              <SearchedProduct
                key={product.goodsNo}
                item={product as LikedProductService}
                index={listIndex}
                checked={checkedProducts.some((item) => item.goodsNo === product.goodsNo)}
                onClickCheckbox={() => onClickCheckbox(product)}
                {...gtmAttr}
                onClickLikeButton={(item, nextIsLiked) => {
                  handleClickLikeButton(item, listIndex, nextIsLiked);
                }}
                callbackClick={(item) => {
                  if (service === SERVICE['29CM']) {
                    track({
                      schema: SNOWPLOW_SCHEME.CLICK_ITEM,
                      data: {
                        page_name: 'curator_select_product',
                        section_name: 'LikedProducts',
                        section_title: '좋아요한 상품',
                        item_no: Number(item.goodsNo),
                        brand_no: Number(item.brandNo),
                        brand_name: item.brandName,
                        large_category_no: item.largeCategoryNo ?? -1,
                        large_category_name: item.largeCategoryName ?? '',
                        middle_category_no: item.middleCategoryNo ?? -1,
                        middle_category_name: item.middleCategoryName ?? '',
                        small_category_no: item.smallCategoryNo ?? -1,
                        small_category_name: item.smallCategoryName ?? '',
                        position: -1,
                        item_position: listIndex + 1,
                      },
                    });
                  }
                }}
                callbackImpression={(item) => {
                  if (service === SERVICE['29CM']) {
                    track({
                      schema: SNOWPLOW_SCHEME.IMPRESSION_ITEM,
                      data: {
                        page_name: 'curator_select_product',
                        section_name: 'LikedProducts',
                        section_title: '좋아요한 상품',
                        item_no: Number(item.goodsNo),
                        brand_no: Number(item.brandNo),
                        brand_name: item.brandName,
                        large_category_no: item.largeCategoryNo ?? -1,
                        large_category_name: item.largeCategoryName ?? '',
                        middle_category_no: item.middleCategoryNo ?? -1,
                        middle_category_name: item.middleCategoryName ?? '',
                        small_category_no: item.smallCategoryNo ?? -1,
                        small_category_name: item.smallCategoryName ?? '',
                        position: -1,
                        item_position: listIndex + 1,
                      },
                    });
                  }
                }}
              />
            );
          })}
          {/* 빈 셀 채우기 */}
          {Array.from({ length: COLUMN_COUNT - row.length }).map((_, emptyIndex) => (
            <div key={`empty-${emptyIndex}`} />
          ))}
        </div>
      );
    },
    [productRows, checkedProducts, onClickCheckbox, handleClickLikeButton, service],
  );

  const computeItemKey = useCallback(
    (index: number) => {
      const row = productRows[index];
      if (!row) return `row-${index}`;
      // row의 모든 goodsNo와 isLiked 상태를 조합하여 고유 키 생성
      return row.map((product) => `${product.goodsNo}-${product.isLiked ? '1' : '0'}`).join('_');
    },
    [productRows],
  );

  const isEmpty = !isPending && products.length === 0 && !hasNextPage && !isFetchingNextPage;

  useEffect(() => {
    onEmptyStateChange?.(isEmpty);
  }, [isEmpty, onEmptyStateChange]);

  // 로딩 상태
  if (isPending) {
    return isWebview ? null : (
      <div className="flex flex-1 items-center justify-center p-4">
        <LoadingDark size="size24x24" />
      </div>
    );
  }

  // 에러 상태
  if (isError) {
    return <EmptyProductList />;
  }

  // 빈 상태 - hasNextPage가 false이고 데이터가 없을 때만 NoGoods 표시
  if (products.length === 0 && !hasNextPage && !isFetchingNextPage) {
    return <EmptyProductList />;
  }

  // 데이터는 없지만 다음 페이지가 있는 경우 - 자동으로 다음 페이지 로드
  if (products.length === 0 && hasNextPage && !isFetchingNextPage) {
    fetchNextPage();
  }

  return (
    <div className={clsx('grow', className)}>
      <Padding x="4" y="3">
        <Typography variant="title18pxMed">좋아요한 상품</Typography>
      </Padding>

      {products.length > 0 && (
        <Virtuoso
          useWindowScroll
          style={{ height: '100%' }}
          totalCount={
            hasNextPage
              ? productRows.length + COLUMN_COUNT // 더 많은 여유 공간으로 endReached 보장
              : productRows.length
          }
          defaultItemHeight={300}
          overscan={10}
          itemContent={renderItemContent}
          computeItemKey={computeItemKey}
          atBottomThreshold={300}
          increaseViewportBy={{ top: 200, bottom: 200 }}
          rangeChanged={onRangeChanged}
        />
      )}
    </div>
  );
}
